import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  getOrCreateConversation,
  sendMessage,
  getAffiliateByName,
} from "../../lib/chatService";
import {
  Calendar,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Users,
  Target,
  Facebook,
  Info,
  Loader2,
  ExternalLink,
  MessageSquare,
  Copy,
  ChevronDown,
  MessageCircle,
  User,
  Upload,
  Eye,
  Rocket,
  Pause,
  RotateCcw,
  Star,
} from "lucide-react";

import {
  SUBMISSION_STATUSES,
  PRIORITY_LEVELS,
  getStatusConfig,
  getPriorityConfig,
  getStatusBadgeProps,
  getPriorityBadgeProps,
  getAvailableTransitions,
  isValidTransition,
} from "../../utils/creativeSubmissionStatuses";

import "../../styles/customScrollbar.css";

const MediaBuyerCreativeSubmissions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [showAllDates, setShowAllDates] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewingSubmission, setReviewingSubmission] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [statusDropdowns, setStatusDropdowns] = useState({});
  const [copySuccess, setCopySuccess] = useState("");
  const [dropdownPositions, setDropdownPositions] = useState({});
  const [chatModalOpen, setChatModalOpen] = useState(null);

  // Chat state
  const [chatMessage, setChatMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, [selectedDate, showAllDates]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".status-dropdown") &&
        !event.target.closest("[data-dropdown-button]") &&
        !event.target.closest("[data-status-dropdown]")
      ) {
        setStatusDropdowns({});
        setDropdownPositions({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);

      console.log(
        "Fetching submissions for date:",
        selectedDate,
        "showAllDates:",
        showAllDates
      );

      // Build the query with JOIN relationships using foreign keys
      let query = supabase
        .from("creative_submissions")
        .select(
          `
          *,
          affiliate_profile:profiles!affiliate_id(id, username, email),
          reviewer_profile:profiles!reviewed_by(id, username, email)
        `
        )
        .order("submitted_at", { ascending: false });

      // Only apply date filtering if not showing all dates
      if (!showAllDates) {
        query = query
          .gte("submitted_at", selectedDate + "T00:00:00")
          .lt(
            "submitted_at",
            new Date(
              new Date(selectedDate + "T00:00:00").getTime() +
                24 * 60 * 60 * 1000
            ).toISOString()
          );
      }

      const { data: submissionsData, error: submissionsError } = await query;

      if (submissionsError) {
        console.error(
          "Error fetching submissions with JOINs:",
          submissionsError
        );
        throw submissionsError;
      }

      console.log("Submissions with joined profiles:", submissionsData);

      if (!submissionsData || submissionsData.length === 0) {
        console.log(
          showAllDates
            ? "No submissions found in database"
            : "No submissions found for this date"
        );
        setSubmissions([]);
        return;
      }

      // Check if any profiles are null and fetch them separately as fallback
      const submissionsWithFallbacks = await Promise.all(
        submissionsData.map(async (submission) => {
          let finalSubmission = { ...submission };

          // If affiliate_profile is null, try to fetch it directly (RLS might be blocking)
          if (!submission.affiliate_profile && submission.affiliate_id) {
            console.warn(
              "⚠️ affiliate_profile is null for submission:",
              submission.id,
              "affiliate_id:",
              submission.affiliate_id
            );
            console.log("🔧 Attempting direct profile fetch as fallback...");

            try {
              const { data: directProfile, error: directError } = await supabase
                .from("profiles")
                .select("id, username, email")
                .eq("id", submission.affiliate_id)
                .single();

              if (directError) {
                console.error(
                  "❌ Direct profile fetch failed:",
                  directError.message
                );
                // Create fallback profile
                finalSubmission.affiliate_profile = {
                  id: submission.affiliate_id,
                  username: `User-${submission.affiliate_id.slice(0, 8)}`,
                  email: "Profile access restricted",
                };
              } else {
                console.log(
                  "✅ Direct profile fetch succeeded:",
                  directProfile
                );
                finalSubmission.affiliate_profile = directProfile;
              }
            } catch (fallbackError) {
              console.error("💥 Fallback profile fetch error:", fallbackError);
              finalSubmission.affiliate_profile = {
                id: submission.affiliate_id,
                username: `User-${submission.affiliate_id.slice(0, 8)}`,
                email: "Profile fetch failed",
              };
            }
          }

          return finalSubmission;
        })
      );

      console.log(
        "Final submissions with profiles and fallbacks:",
        submissionsWithFallbacks
      );
      setSubmissions(submissionsWithFallbacks);
    } catch (error) {
      console.error("Error fetching submissions:", error);

      // Additional debugging for RLS issues
      if (
        error.message?.includes("permission denied") ||
        error.message?.includes("row-level security")
      ) {
        console.error(
          "🔒 This appears to be a Row Level Security (RLS) issue. Media buyers may not have permission to view profiles."
        );
        console.error(
          "💡 Solution: Run the migration to add media buyer profile access policy."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmission = async (submissionId, status, notes = "") => {
    try {
      // Use the new status update function for better tracking
      const { data, error } = await supabase.rpc("update_submission_status", {
        p_submission_id: submissionId,
        p_new_status: status,
        p_reviewer_id: user.id,
        p_notes: notes,
      });

      if (error) throw error;

      // Refresh submissions to get latest data
      fetchSubmissions();
      setReviewingSubmission(null);
      setReviewNotes("");

      // Close dropdown
      setStatusDropdowns((prev) => ({ ...prev, [submissionId]: false }));

      console.log(
        `Submission ${submissionId} reviewed with status: ${status}`,
        data
      );
    } catch (error) {
      console.error("Error reviewing submission:", error);
      alert("Failed to review submission. Please try again.");
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopySuccess("Link copied!");
      setTimeout(() => setCopySuccess(""), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  const toggleStatusDropdown = (submissionId) => {
    if (statusDropdowns[submissionId]) {
      // Closing dropdown
      setStatusDropdowns((prev) => ({
        ...prev,
        [submissionId]: false,
      }));
      setDropdownPositions((prev) => ({
        ...prev,
        [submissionId]: null,
      }));
    } else {
      // Opening dropdown - calculate position (prefer visible element)
      const candidates = Array.from(
        document.querySelectorAll(`[data-dropdown-button="${submissionId}"]`)
      );
      const buttonElement =
        candidates.find((el) => el.offsetParent !== null) || candidates[0];

      if (buttonElement) {
        const rect = buttonElement.getBoundingClientRect();
        // Clamp to viewport so it doesn't go off-screen on mobile
        const estimatedWidth = 200; // approx dropdown width
        const margin = 8;
        const left = Math.max(
          margin,
          Math.min(rect.left, window.innerWidth - estimatedWidth - margin)
        );
        setDropdownPositions((prev) => ({
          ...prev,
          [submissionId]: {
            top: rect.bottom + 4,
            left,
          },
        }));
      }

      setStatusDropdowns((prev) => ({
        ...prev,
        [submissionId]: true,
      }));
    }
  };

  const handleStatusChange = (submissionId, newStatus) => {
    if (newStatus === "rejected") {
      setReviewingSubmission(submissionId);
    } else {
      handleReviewSubmission(submissionId, newStatus);
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const toggleShowAllDates = () => {
    setShowAllDates(!showAllDates);
  };

  const handleExportCSV = () => {
    // CSV export logic
    console.log("Exporting CSV for date:", selectedDate);
  };

  const handleOpenChat = (affiliateId, affiliateName) => {
    setChatModalOpen({ affiliateId, affiliateName });
  };

  const handleCloseChat = () => {
    setChatModalOpen(null);
    setChatMessage("");
    setSendingMessage(false);
  };

  // Handle sending chat message
  const handleSendChatMessage = async () => {
    if (!chatMessage.trim() || sendingMessage) return;

    setSendingMessage(true);
    try {
      console.log(`🚀 Sending message to ${chatModalOpen.affiliateName}`);

      // 1. Get the affiliate by name from the modal
      const affiliate = await getAffiliateByName(chatModalOpen.affiliateName);
      if (!affiliate) {
        alert(`Could not find affiliate ${chatModalOpen.affiliateName}`);
        return;
      }

      // 2. Create or get conversation for default brand (holistic_hercules)
      const conversation = await getOrCreateConversation(
        affiliate.id,
        "holistic_hercules", // Default brand for creative submissions
        user.id
      );

      if (!conversation || !conversation.conversation_id) {
        alert("Failed to create conversation. Please try again.");
        return;
      }

      // 3. Send the message
      await sendMessage(
        conversation.conversation_id,
        user.id,
        chatMessage.trim(),
        "text"
      );

      // 4. Clear the input and show success
      setChatMessage("");
      alert(
        "Message sent successfully! You can continue the conversation in the OlympusChat tab."
      );

      // Optionally close the modal
      handleCloseChat();

      console.log(`✅ Message sent to ${affiliate.username}`);
    } catch (error) {
      console.error("❌ Error sending message:", error);
      alert(`Failed to send message: ${error.message}`);
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle Enter key press in chat input
  const handleChatKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendChatMessage();
    }
  };

  const getStatusIcon = (status) => {
    const config = getStatusConfig(status);
    const Icon = config.icon;
    return <Icon className={`w-5 h-5 ${config.color}`} />;
  };

  const getStatusColor = (status) => {
    const config = getStatusConfig(status);
    return `${config.color} ${config.bgColor} ${config.borderColor}`;
  };

  return (
    <div className="space-y-6 relative z-0">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={toggleShowAllDates}
            className={`px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 ${
              showAllDates
                ? "bg-[#B8460E] text-white hover:bg-[#8B3A0E]"
                : "bg-white/10 backdrop-blur-xl border border-white/20 text-white hover:bg-white/20"
            }`}
          >
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="text-sm sm:text-base">
              {showAllDates ? "Show Today Only" : "Show All Dates"}
            </span>
          </button>
          <div className="relative">
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              disabled={showAllDates}
              className={`px-3 py-2 sm:px-4 sm:py-3 backdrop-blur-xl border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent ${
                showAllDates
                  ? "bg-gray-600/50 border-gray-500/50 text-gray-400 cursor-not-allowed"
                  : "bg-white/10 border-white/20 text-white"
              }`}
            />
          </div>
        </div>
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 sm:px-6 sm:py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl text-white hover:bg-white/20 transition-all duration-200 flex items-center gap-2 text-sm sm:text-base"
        >
          <Download className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Copy Success Notification */}
      {copySuccess && (
        <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {copySuccess}
        </div>
      )}

      {/* Global Status Dropdowns - Rendered at top level to escape stacking contexts */}
      {Object.entries(statusDropdowns).map(([submissionId, isOpen]) =>
        isOpen && dropdownPositions[submissionId] ? (
          <div
            key={submissionId}
            className="fixed bg-gray-900/98 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl z-[99999] w-40 overflow-hidden"
            data-status-dropdown="true"
            style={{
              top: dropdownPositions[submissionId].top,
              left: dropdownPositions[submissionId].left,
              minWidth: "180px",
              boxShadow: `
                0 20px 25px -5px rgba(0, 0, 0, 0.5),
                0 10px 10px -5px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `,
            }}
          >
            <div className="p-2 max-h-80 overflow-y-auto custom-scrollbar">
              {(() => {
                const currentSubmission = submissions.find(
                  (s) => s.id === submissionId
                );
                const currentStatus = currentSubmission?.status || "pending";
                const availableTransitions =
                  getAvailableTransitions(currentStatus);

                return availableTransitions.map((statusKey) => {
                  const statusConfig = getStatusConfig(statusKey);
                  const Icon = statusConfig.icon;

                  return (
                    <button
                      key={statusKey}
                      onClick={() =>
                        handleStatusChange(submissionId, statusKey)
                      }
                      className={`w-full text-left px-3 py-2.5 text-sm hover:bg-white/10 rounded-lg transition-all duration-200 flex items-center gap-3 group ${statusConfig.color} hover:scale-[1.02] active:scale-[0.98]`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                      {statusConfig.label}
                    </button>
                  );
                });
              })()}

              {/* Divider */}
              <div className="relative my-3">
                <div className="border-t border-white/10"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
              </div>

              {/* Priority Actions */}
              <div className="px-3 py-2 bg-white/5 backdrop-blur-sm">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  Quick Actions
                </span>
              </div>

              <button
                onClick={() => handleStatusChange(submissionId, "in_review")}
                className="w-full text-left px-3 py-2.5 text-sm text-purple-400 hover:bg-white/10 rounded-lg transition-all duration-200 flex items-center gap-3 group hover:scale-[1.02] active:scale-[0.98]"
              >
                <Eye className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                Start Review
              </button>

              <button
                onClick={() => handleStatusChange(submissionId, "approved")}
                className="w-full text-left px-3 py-2.5 text-sm text-green-400 hover:bg-white/10 rounded-lg transition-all duration-200 flex items-center gap-3 group hover:scale-[1.02] active:scale-[0.98]"
              >
                <CheckCircle className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                Approve
              </button>

              <button
                onClick={() =>
                  handleStatusChange(submissionId, "changes_needed")
                }
                className="w-full text-left px-3 py-2.5 text-sm text-orange-400 hover:bg-white/10 rounded-lg transition-all duration-200 flex items-center gap-3 group hover:scale-[1.02] active:scale-[0.98]"
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                Request Changes
              </button>

              <button
                onClick={() => handleStatusChange(submissionId, "rejected")}
                className="w-full text-left px-3 py-2.5 text-sm text-red-400 hover:bg-white/10 rounded-lg transition-all duration-200 flex items-center gap-3 group hover:scale-[1.02] active:scale-[0.98]"
              >
                <XCircle className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                Reject
              </button>
            </div>
          </div>
        ) : null
      )}

      {/* Submissions Content */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 pb-12 relative z-0">
        <h2 className="text-xl font-bold text-white mb-4">
          {showAllDates
            ? "All Creative Submissions"
            : `Creative Submissions for ${new Date(selectedDate).toLocaleDateString()}`}
        </h2>
        <p className="text-gray-300 mb-6">
          {submissions.length} submission{submissions.length !== 1 ? "s" : ""}{" "}
          found
          {showAllDates && submissions.length > 0 && (
            <span className="text-[#B8460E] ml-2">(from all dates)</span>
          )}
        </p>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-16 h-16 text-[#B8460E] animate-spin mb-4" />
            <p className="text-gray-400">Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mb-4" />
            <p className="text-gray-400">No submissions found for this date</p>
          </div>
        ) : (
          <div className="hidden sm:block bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl overflow-visible relative z-10">
            {/* Table Header */}
            <div className="bg-black/50 border-b border-white/10 px-6 py-4">
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-2">
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                    Username
                  </h3>
                </div>
                <div className="col-span-4">
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                    Link
                  </h3>
                </div>
                <div className="col-span-2">
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                    Date Submitted
                  </h3>
                </div>
                <div className="col-span-2">
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                    Status
                  </h3>
                </div>
                <div className="col-span-2">
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                    Chat
                  </h3>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-white/10 overflow-visible">
              {submissions.map((submission) => (
                <div key={submission.id}>
                  <div
                    className={`px-6 py-4 hover:bg-white/5 transition-all duration-200`}
                  >
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Username Column */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-3">
                          {/* Profile Picture */}
                          <div className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/20 overflow-hidden flex-shrink-0">
                            {submission.affiliate_profile?.profile_photo_url ? (
                              <img
                                src={
                                  submission.affiliate_profile.profile_photo_url
                                }
                                alt={
                                  submission.affiliate_profile?.username ||
                                  "User"
                                }
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-white" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-white font-medium truncate">
                              {submission.affiliate_profile?.username ||
                                `User-${submission.affiliate_id?.slice(0, 8) || "Unknown"}`}
                            </div>
                            <div className="text-xs text-gray-400">
                              Affiliate
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Link Column */}
                      <div className="col-span-4">
                        <div className="flex items-center space-x-2">
                          <a
                            href={submission.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors group flex-1"
                          >
                            <ExternalLink className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm font-medium truncate group-hover:underline">
                              {submission.link_url.length > 40
                                ? `${submission.link_url.substring(0, 40)}...`
                                : submission.link_url}
                            </span>
                          </a>
                          <button
                            onClick={() => handleCopyLink(submission.link_url)}
                            className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-all duration-200"
                            title="Copy link"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Date Column */}
                      <div className="col-span-2">
                        <div className="text-sm text-gray-300">
                          <div className="font-medium">
                            {new Date(
                              submission.submitted_at
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(
                              submission.submitted_at
                            ).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Status Column */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(submission.status)}
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(submission.status)}`}
                          >
                            {getStatusConfig(submission.status).label}
                          </span>
                          {/* Show revision count if applicable */}
                          {submission.revision_count > 0 && (
                            <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded border border-orange-500/30">
                              Rev {submission.revision_count}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Chat Column */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() =>
                              handleOpenChat(
                                submission.affiliate_id,
                                submission.affiliate_profile?.username ||
                                  `User-${submission.affiliate_id?.slice(0, 8)}`
                              )
                            }
                            className="flex items-center space-x-2 px-3 py-2 bg-[#B8460E] hover:bg-[#8B3A0E] rounded-lg transition-all duration-200 text-sm text-white"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>Chat</span>
                          </button>
                          <div className="relative status-dropdown z-[9999]">
                            <button
                              onClick={() =>
                                toggleStatusDropdown(submission.id)
                              }
                              className="flex items-center space-x-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 text-sm text-white"
                              data-dropdown-button={submission.id}
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Review Notes Section */}
                  {submission.reviewer_notes && (
                    <div className="px-6 pb-4">
                      <div className="bg-black/30 rounded-lg p-3">
                        <div className="flex items-center space-x-2 mb-1">
                          <MessageSquare className="w-4 h-4 text-[#B8460E]" />
                          <span className="text-[#B8460E] font-semibold text-sm">
                            Review Notes:
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm">
                          {submission.reviewer_notes}
                        </p>
                        {submission.reviewer_profile && (
                          <p className="text-gray-400 text-xs mt-1">
                            Reviewed by:{" "}
                            {submission.reviewer_profile?.username ||
                              "Unknown Reviewer"}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Review Modal */}
                  {reviewingSubmission === submission.id && (
                    <div className="px-6 pb-4">
                      <div className="bg-red-900/20 border border-red-400/30 rounded-lg p-4">
                        <h4 className="text-red-200 font-semibold mb-3">
                          Reject Submission
                        </h4>
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          placeholder="Provide feedback for improvement..."
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                          rows={3}
                        />
                        <div className="flex items-center space-x-3 mt-3">
                          <button
                            onClick={() =>
                              handleReviewSubmission(
                                submission.id,
                                "rejected",
                                reviewNotes
                              )
                            }
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200"
                          >
                            Confirm Reject
                          </button>
                          <button
                            onClick={() => {
                              setReviewingSubmission(null);
                              setReviewNotes("");
                            }}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all duration-200"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Mobile cards */}
        {!loading && submissions.length > 0 && (
          <div className="sm:hidden space-y-3">
            {submissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center border border-white/20 overflow-hidden flex-shrink-0">
                      {submission.affiliate_profile?.profile_photo_url ? (
                        <img
                          src={submission.affiliate_profile.profile_photo_url}
                          alt={submission.affiliate_profile?.username || "User"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate text-sm">
                        {submission.affiliate_profile?.username ||
                          `User-${submission.affiliate_id?.slice(0, 8) || "Unknown"}`}
                      </div>
                      <div className="text-[11px] text-gray-400">Affiliate</div>
                    </div>
                  </div>
                  <div
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${getStatusColor(submission.status)}`}
                  >
                    {getStatusConfig(submission.status).label}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <ExternalLink className="w-4 h-4 text-blue-400" />
                  <a
                    href={submission.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 text-sm truncate flex-1"
                  >
                    {submission.link_url}
                  </a>
                  <button
                    onClick={() => handleCopyLink(submission.link_url)}
                    className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-gray-300">
                    <div className="font-medium">
                      {new Date(submission.submitted_at).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric", year: "numeric" }
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      {new Date(submission.submitted_at).toLocaleTimeString(
                        "en-US",
                        { hour: "2-digit", minute: "2-digit" }
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        handleOpenChat(
                          submission.affiliate_id,
                          submission.affiliate_profile?.username ||
                            `User-${submission.affiliate_id?.slice(0, 8)}`
                        )
                      }
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#B8460E] hover:bg-[#8B3A0E] rounded-lg text-white text-sm"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>Chat</span>
                    </button>
                    <div className="relative status-dropdown z-[9999]">
                      <button
                        onClick={() => toggleStatusDropdown(submission.id)}
                        className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-white"
                        data-dropdown-button={submission.id}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                {submission.reviewer_notes && (
                  <div className="mt-3 text-xs text-gray-300">
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="w-4 h-4 text-[#B8460E]" />
                      <span className="text-[#B8460E] font-semibold">
                        Review Notes:
                      </span>
                    </div>
                    <p>{submission.reviewer_notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Instructions */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Info className="w-6 h-6 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">
            Review Instructions
          </h3>
        </div>
        <ul className="space-y-2 text-gray-300">
          <li>• Review all submissions daily before 11 PM EST</li>
          <li>
            • Use "Quick Approve" for submissions that meet all requirements
          </li>
          <li>
            • Use "Reject" to provide feedback when creatives need revision
          </li>
          <li>
            • Add detailed notes to help affiliates improve future submissions
          </li>
        </ul>
      </div>

      {/* Facebook Creative Launch Instructions */}
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Facebook className="w-6 h-6 text-blue-500" />
          <h3 className="text-lg font-semibold text-white">
            Facebook Creative Launch Instructions
          </h3>
        </div>

        {/* Naming Convention */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <Target className="w-5 h-5 text-[#B8460E]" />
            <h4 className="font-semibold text-white">
              Naming Convention (CRITICAL)
            </h4>
          </div>
          <p className="text-gray-300 mb-4">
            Use the affiliate's rolling naming convention exactly as they
            provide it.{" "}
            <strong className="text-[#B8460E]">Never reset the numbers!</strong>
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Correct Format */}
            <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="font-semibold text-green-400">
                  Correct Format:
                </span>
              </div>
              <div className="font-mono text-sm text-green-300 space-y-1">
                <div>Jack1, Jack2, Jack3, Jack4...</div>
                <div>Sarah15, Sarah16, Sarah17...</div>
                <div>Mike247, Mike248, Mike249...</div>
              </div>
            </div>

            {/* Wrong Format */}
            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="font-semibold text-red-400">
                  Wrong Format:
                </span>
              </div>
              <div className="font-mono text-sm text-red-300 space-y-1">
                <div>Jack 1 (no spaces)</div>
                <div>jack1 (wrong capitalization)</div>
                <div>Jack_1 (no underscores)</div>
                <div>Jack-1 (no dashes)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Rolling Counter System */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <Users className="w-5 h-5 text-[#B8460E]" />
            <h4 className="font-semibold text-white">Rolling Counter System</h4>
          </div>
          <ol className="space-y-2 text-gray-300">
            <li>1. Each affiliate has their own continuous number sequence</li>
            <li>2. Numbers never reset - they keep counting up forever</li>
            <li>
              3. If Jack's last creative was Jack247, the next ones are Jack248,
              Jack249, etc.
            </li>
            <li>
              4. Use the exact names from the affiliate's Google Drive folder
            </li>
          </ol>
        </div>

        {/* Facebook Launch Process */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <FileText className="w-5 h-5 text-[#B8460E]" />
            <h4 className="font-semibold text-white">
              Facebook Launch Process
            </h4>
          </div>
          <ol className="space-y-2 text-gray-300">
            <li>1. Download creatives from the approved Google Drive link</li>
            <li>
              2. Create Facebook ads using the exact file names as ad names
            </li>
            <li>3. Example: file "Jack248.mp4" becomes ad name "Jack248"</li>
            <li>
              4. Maintain the rolling sequence for tracking and optimization
            </li>
          </ol>
        </div>

        {/* Important Reminder */}
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <span className="font-semibold text-yellow-400">
              Important Reminder
            </span>
          </div>
          <p className="text-yellow-300 text-sm">
            The naming convention is critical for performance tracking. Always
            use the affiliate's exact naming format with no modifications.
          </p>
        </div>
      </div>

      {/* Chat Modal */}
      {chatModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <MessageCircle className="w-6 h-6 text-[#B8460E]" />
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Chat with {chatModalOpen.affiliateName}
                  </h3>
                  <p className="text-sm text-gray-400">Direct message</p>
                </div>
              </div>
              <button
                onClick={handleCloseChat}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="text-center text-gray-400 py-8">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                <p className="text-lg font-medium mb-2">Start a conversation</p>
                <p className="text-sm">
                  Send a message to {chatModalOpen.affiliateName} about their
                  creative submission.
                </p>
              </div>
            </div>

            {/* Chat Input */}
            <div className="p-6 border-t border-white/10">
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  placeholder={`Message ${chatModalOpen.affiliateName}...`}
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={handleChatKeyPress}
                />
                <button
                  onClick={handleSendChatMessage}
                  className="px-6 py-3 bg-[#B8460E] hover:bg-[#8B3A0E] text-white rounded-lg transition-all duration-200 font-medium flex items-center"
                  disabled={sendingMessage || !chatMessage.trim()}
                >
                  {sendingMessage ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    "Send"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaBuyerCreativeSubmissions;
